<template>
  <div
    class="relative flex max-w-[calc(50%)] flex-[0_0_50%] flex-col self-stretch border border-b-0 border-l-[0] border-neutral-200 duration-[0.5s] hover:z-[1] hover:shadow-2xl lg:max-w-[calc(33%)] lg:flex-[1_1_33%] [&:nth-last-child(-n+2)]:!border-b [&:nth-last-child(-n+3)]:desktop:!border-b"
  >
    <div class="flex h-full flex-col justify-between">
      <div
        class="relative border-b border-neutral-100"
        @mouseenter="alternativeImage.show = true"
      >
        <NuxtLink
          :aria-label="productToDisplay.title"
          :to="productLink || ''"
          class="block"
          @click="handleProductTrackingClick"
        >
          <NuxtImg
            v-if="imageLink?.[0]"
            class="h-auto w-full min-w-52 max-w-md transition-opacity duration-500 ease-in-out mobile:min-w-full"
            :class="isHovered && alternativeImage.isLoaded ? 'opacity-0' : 'opacity-100'"
            preset="plp"
            :src="imageLink[0]"
            :alt="productToDisplay.name"
            :loading="imageLoadType"
            :placeholder="[445, 532]"
            width="445"
            height="532"
            @mouseenter="isHovered = true"
            @mouseleave="isHovered = false"
          />
          <NuxtImg
            v-if="imageLink?.[1] && alternativeImage.show"
            class="absolute top-0 h-auto w-full min-w-52 max-w-md transition-opacity duration-500 ease-in-out mobile:min-w-full"
            :class="isHovered && alternativeImage.isLoaded ? 'opacity-100' : 'opacity-0'"
            preset="plp"
            :src="imageLink[1]"
            :alt="productToDisplay.name"
            :placeholder="[445, 532]"
            width="445"
            height="532"
            @mouseenter="isHovered = true"
            @mouseleave="isHovered = false"
            @load="alternativeImage.isLoaded = true"
          />
        </NuxtLink>
        <KikButton
          v-if="$flags.hasnt('enabledKiosk') && $flags.has('shoppinglist.wishlist')"
          variant="tertiary"
          size="base"
          square
          class="absolute right-0 top-0 !rounded-full hover:!bg-neutral-200"
          :aria-label="$t('Add to wishlist')"
          @click="handleToggleToWishlist(productToDisplay)"
        >
          <component
            :is="isInWishlist(productToDisplay) ? SfIconFavoriteFilled : SfIconFavorite"
            size="base"
            :class="isInWishlist(productToDisplay) ? 'fill-primary' : 'fill-neutral-500'"
          />
        </KikButton>
        <div class="absolute left-0 top-0">
          <div
            v-if="productToDisplay.isNew"
            class="relative mb-2 w-fit bg-neutral-900 px-3 py-1.5 text-sm text-white"
          >
            {{ $t('New') }}
          </div>
          <div
            v-if="productToDisplay.isOnlineOnly"
            class="relative mb-2 bg-neutral-900 px-3 py-1.5 text-sm text-white"
          >
            {{ $t('Online Only') }}
          </div>
          <div
            v-if="salePercentage"
            class="relative mb-2 w-fit bg-primary-700 px-3 py-1.5 text-sm text-white"
          >
            -{{ salePercentage }}%
            <span v-if="isOnlineSale">{{ $t('only Online') }}</span>
          </div>
        </div>
        <div
          v-if="productToDisplay.attributes.marketing_tag"
          class="absolute bottom-2 left-3 w-fit mobile:text-xs"
          :class="{ 'mb-8 mobile:mb-6': productToDisplay.isPlusSize }"
        >
          <SharedMarketingTags
            pdp-or-plp="plp"
            :marketing-tags="productToDisplay.attributes.marketing_tag"
          />
        </div>
        <div
          v-if="productToDisplay.isPlusSize"
          class="absolute bottom-2 left-3 bg-neutral-700 px-3 py-1.5 text-sm text-white mobile:px-2 mobile:text-xs"
        >
          {{ $t('Plus size') }}
        </div>
      </div>
      <NuxtLink
        :aria-label="productToDisplay.name"
        :to="productLink || ''"
        variant="secondary"
        class="px-4 pb-0.5 pt-2"
        @click="handleProductTrackingClick"
      >
        <div class="mb-2 hidden items-center gap-1 desktop:flex">
          <div
            v-for="(color, index) in colors.slice(0, 4)"
            :key="index"
            class="max-w-[60px] flex-1 cursor-pointer border border-neutral-200"
          >
            <NuxtImg
              class="object-cover"
              :src="color.images?.[0]?.url"
              :placeholder="[60, 71]"
              width="60"
              height="71"
              :loading="imageLoadType"
              :alt="`${productToDisplay.name} ${color.label}`"
              tabindex="0"
              @click.prevent="handleProductClick(color)"
              @mouseover.prevent="handleColorSelection(color)"
              @keydown.space.prevent="handleProductClick(color)"
              @keydown.enter.prevent="handleProductClick(color)"
            />
          </div>
          <p
            v-if="colors.length > 4"
            class="flex aspect-square size-full max-w-[60px] flex-1 cursor-pointer items-center justify-center rounded-full border border-neutral-200 text-center text-base text-neutral-900"
            @click="handleProductClick"
          >
            {{ `+${colors.length - 4}` }}
          </p>
        </div>
        <div class="mb-2 flex items-center gap-1 desktop:hidden">
          <div
            v-for="(color, index) in colors.slice(0, 2)"
            :key="index"
            class="max-w-[51px] flex-1 cursor-pointer border border-neutral-200"
          >
            <NuxtImg
              class="object-cover"
              :src="color.images?.[0]?.url"
              :placeholder="[51, 61]"
              width="51"
              height="61"
              :loading="imageLoadType"
              :alt="`${productToDisplay.name} ${color.label}`"
              tabindex="0"
              @click.prevent="handleProductClick(color)"
              @mouseover.prevent="handleColorSelection(color)"
              @keydown.space.prevent="handleProductClick(color)"
              @keydown.enter.prevent="handleProductClick(color)"
            />
          </div>
          <p
            v-if="colors.length > 2"
            class="flex aspect-square size-full max-w-[51px] flex-1 cursor-pointer items-center justify-center rounded-full border border-neutral-200 text-center text-base text-neutral-900"
            @click="handleProductClick"
          >
            {{ `+${colors.length - 2}` }}
          </p>
        </div>

        <NuxtLink
          v-if="productToDisplay.name"
          :aria-label="productToDisplay.name"
          :to="productLink || ''"
          variant="secondary"
          class="text-[1.25rem] font-bold leading-6 no-underline hover:text-primary-700 mobile:break-words"
          @click="handleProductTrackingClick"
        >
          {{ productToDisplay.name }}
        </NuxtLink>
        <p class="min-h-5 text-sm font-normal leading-5 text-neutral-700 mobile:break-words mobile:pb-1">
          {{ introduction }}
        </p>
        <KikEtrustedWidget
          id="plp"
          class="mt-1"
          :sku="productToDisplay.key"
          @click="handleProductClick"
        />
      </NuxtLink>
      <NuxtLink
        :aria-label="productToDisplay.name"
        :to="productLink || ''"
        variant="secondary"
        class="relative mt-auto h-full px-4 pt-0.5"
        @click="handleProductTrackingClick"
      >
        <p
          v-if="productToDisplay.showFromPrice"
          class="text-xs text-neutral-400"
        >
          {{ $t('from') }}
        </p>
        <div class="flex h-full items-end gap-3 px-4 pb-8 desktop:pb-6">
          <KikPrice
            class="text-lg font-semibold"
            :regular="
              productToDisplay.attributes?.discount_sale_value
                && productToDisplay.price.strike
                && $n(productToDisplay.price.strike, 'currency')
            "
            :special="finalPrice && $n(finalPrice, 'currency')"
          />
          <NuxtImg
            v-if="productToDisplay.isMyKikDiscount"
            :alt="$t('myKiK')"
            title="myKiK"
            src="/img/mykik.svg"
            width="47"
          />
        </div>
        <div class="absolute bottom-0 flex h-8 flex-wrap items-center desktop:h-6">
          <template v-if="productToDisplay.price.unit">
            <p class="separable text-[10px] text-neutral-400 mobile:leading-[10px]">
              ({{ productToDisplay.price.unit }})
            </p>
          </template>
          <template v-if="productToDisplay.price.deposit">
            <p class="separable text-[10px] text-neutral-400 mobile:leading-[10px]">
              {{ $t('+ {amount} deposit', { amount: $n(productToDisplay.price.deposit, 'currency') }) }}
            </p>
          </template>
          <template v-if="productToDisplay.minimumAmount > 1">
            <p class="separable text-[10px] text-neutral-400 mobile:leading-[10px]">
              {{ $t('Minimum order quantity x pieces', { amount: productToDisplay.minimumAmount }) }}
            </p>
          </template>
          <p
            v-if="productToDisplay.price.lowestTotalPrice"
            class="separable text-[10px] text-neutral-400 mobile:leading-[10px]"
          >
            {{ $t('30 Day Best Price') }}<sup>1</sup>:
            {{ $n(productToDisplay.price.lowestTotalPrice.value, 'currency') }}
            <template v-if="productToDisplay.price.lowestTotalPrice.percentage">
              ({{ productToDisplay.price.lowestTotalPrice.percentage }}%)
            </template>
          </p>
        </div>
      </NuxtLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { SfIconFavorite, SfIconFavoriteFilled } from '@storefront-ui/vue';
import productGetters from '~/composables/getters/productGetters';
import { useProductCard } from '~/composables/useProductCard/useProductCard';
import { useAppLocale } from '~/composables/custom/useAppLocale';
import { useUser } from '~/composables/custom/useUser/useUser';
import { useTracking } from '~/composables/custom/useTracking';

interface Props {
  product: any;
  isSlider?: boolean;
  loading?: HTMLImageElement['loading'];
}

const props = withDefaults(defineProps<Props>(), {
  isSlider: false,
  loading: 'lazy',
});

const { $i18n, $domain, $flags } = useNuxtApp();
const { isMobile } = useDevice();
const { locale } = useAppLocale();
const { isAuthenticated } = useUser();
const { toggleLoginDrawer } = useUiState();
const { trackSelectItem } = useTracking();
const { result: category } = useCategory();

const { toggleToWishlist, isInWishlist, addToQueue } = useWishlist();

const alternativeImage = reactive({
  visibleIndex: 0,
  show: false,
  isLoaded: false,
});
const isHovered = ref(false);

const { getProduct, result: productForCard } = useProductCard(props.product?.id);

const loading = toRef(props, 'loading');
const imageLoadType = inject<HTMLImageElement['loading']>('imageLoadType', loading.value);

const selectedColor = ref();

const productToDisplay = computed(() => {
  const variant
    = productForCard.value?.variants.find((variant: { attributes: { color_code: string } }) => variant.attributes.color_code === selectedColor.value) ?? {};
  return {
    ...props.product,
    ...variant,
  };
});

const isOnlineSale = computed(() => productToDisplay.value?.isOnlineSale);

const colors = computed<any[]>(() => {
  return productToDisplay.value?.colors.map((color: any, index: any) => ({
    ...color,
    selected: selectedColor.value ? selectedColor.value === color.value : index === 0,
  }));
});

const salePercentage = computed<number>(
  () =>
    productToDisplay.value?.attributes?.discount_sale_value
    && !productToDisplay.value?.price?.hideDiscountSaleValue
    && Number(productToDisplay.value?.attributes?.discount_sale_value),
);

const introduction = computed(() => {
  const { brandName = '' } = productToDisplay.value;
  const {
    capacity_uom: capacityUnit,
    capacity_quantity: capacityQuantity,
    detailname,
  } = productToDisplay.value.attributes || {};
  const capacityLabel = capacityUnit === 'Stk' ? $i18n.t(capacityUnit, { amount: capacityQuantity }) : null;

  const labelArray = [];

  if (capacityLabel) {
    labelArray.push(capacityLabel);
  }
  else if (capacityQuantity && capacityUnit) {
    labelArray.push(
      `${Intl.NumberFormat($domain.countryCode).format(capacityQuantity) ?? ''} ${$i18n.t(capacityUnit ?? '')}`,
    );
  }
  if (brandName) {
    labelArray.push(brandName);
  }
  if (detailname) {
    labelArray.push(detailname);
  }

  return labelArray.join(', ');
});
const productLink = computed(() =>
  productGetters.getProductUrl({ key: productToDisplay.value?.key, hrefLang: productToDisplay.value?.hrefLang }, locale),
);
const imageLink = computed(() => {
  const [firstImage, secondImage]: { url: string }[]
    = colors.value?.find(color => color.value === selectedColor.value)?.images ?? productToDisplay.value?.images ?? [];
  const links = new Set([firstImage?.url]);
  if (secondImage && !isMobile) {
    links.add(secondImage.url);
  }
  return [...links];
});

const finalPrice = computed(() =>
  productToDisplay.value.price.deposit
    ? productToDisplay.value.price.final - productToDisplay.value.price.deposit
    : productToDisplay.value.price.final,
);

const handleColorSelection = async (color: any) => {
  if (selectedColor.value === color.value) {
    return;
  }
  selectedColor.value = color.value;

  await getProduct();
};

const handleProductClick = async (color?: any) => {
  if (color) {
    await handleColorSelection(color);
  }

  await getProduct();
  navigateTo(productGetters.getProductUrl(productToDisplay.value, locale));
};

const handleToggleToWishlist = (product: any) => {
  if (isAuthenticated.value) {
    toggleToWishlist(product);
  }
  else {
    toggleLoginDrawer();
    addToQueue(product);
  }
};

const handleProductTrackingClick = () => {
  trackSelectItem(category.value, props.product);
};
</script>
